import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Package, 
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  ChefHat,
  FileText,
  Edit3
} from 'lucide-react';

export const BalirnaVerifyPage = () => {
  const { productionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [production, setProduction] = useState(null);
  const [kitchenHandover, setKitchenHandover] = useState(null);
  const [corrections, setCorrections] = useState({});
  const [notes, setNotes] = useState('');
  const [showCorrections, setShowCorrections] = useState(false);

  useEffect(() => {
    fetchData();
  }, [productionId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/kitchen/handover/${productionId}`);
      
      setProduction(response.data.production);
      setKitchenHandover(response.data.handover);
      
      // Initialize corrections with original values (use quantity = zadaná hodnota)
      const initialCorrections = {};
      response.data.handover?.items?.forEach(item => {
        initialCorrections[item.item_id] = item.quantity;
      });
      setCorrections(initialCorrections);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Chyba při načítání dat');
      navigate('/balirna');
    } finally {
      setLoading(false);
    }
  };

  // Check if any corrections have been made
  const hasCorrections = () => {
    if (!kitchenHandover?.items) return false;
    return kitchenHandover.items.some(item => {
      const correctedValue = parseFloat(corrections[item.item_id]);
      return !isNaN(correctedValue) && correctedValue !== item.quantity;
    });
  };

  const handleVerify = async (withCorrections = false) => {
    try {
      setSaving(true);
      
      const correctionData = withCorrections ? 
        kitchenHandover.items.map(item => ({
          item_id: item.item_id,
          name: item.name,
          unit: item.unit,
          original_quantity: item.quantity,
          received_quantity: parseFloat(corrections[item.item_id]) || item.quantity
        })).filter(c => c.original_quantity !== c.received_quantity) : [];
      
      // Validate: if there are corrections, notes are required
      if (correctionData.length > 0 && !notes.trim()) {
        toast.error('Při zadání korekce je poznámka povinná');
        setSaving(false);
        return;
      }
      
      await api.post(`/balirna/verify-handover/${productionId}`, {
        corrections: correctionData,
        notes: notes
      });
      
      toast.success('Převzetí potvrzeno');
      // Navigate to create inspection
      navigate(`/balirna/new/${productionId}`);
      
    } catch (error) {
      console.error('Error verifying handover:', error);
      toast.error('Chyba při potvrzování převzetí');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!kitchenHandover || !kitchenHandover.confirmed) {
    return (
      <Layout>
        <div className="p-6 max-w-4xl mx-auto">
          <Card>
            <CardContent className="py-12 text-center">
              <AlertTriangle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">
                Kuchyně zatím nepředala
              </h3>
              <p className="text-slate-400 mb-4">
                Tento záznam ještě nemá potvrzenou předávku z kuchyně.
              </p>
              <Button onClick={() => navigate('/balirna')}>
                Zpět na Balírnu
              </Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/balirna')}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Package className="w-7 h-7 text-orange-600" />
              Převzetí z kuchyně
            </h1>
            <p className="text-slate-500 mt-1">{production?.doklad}</p>
          </div>
        </div>

        {/* Production info */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Informace o výrobku
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500">Výrobek</p>
                <p className="font-medium">{production?.data?.Popis || production?.data?.Výrobek || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Šarže</p>
                <p className="font-medium">{production?.data?.Šarže || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Plánované množství</p>
                <p className="font-medium text-blue-600">{production?.data?.['Množ.plán.'] || '-'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Kitchen handover details */}
        <Card className="mb-6 border-orange-200 bg-orange-50/30">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-700">
                <ChefHat className="w-5 h-5" />
                Předávka z kuchyně
              </CardTitle>
              <Badge className="bg-green-100 text-green-700">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Potvrzeno {new Date(kitchenHandover.confirmed_at).toLocaleDateString('cs-CZ')}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Předal: <span className="font-medium">{kitchenHandover.confirmed_by_name}</span>
            </p>
            
            {/* Handover items */}
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-slate-700 mb-3">Předané položky:</h4>
              <div className="space-y-3">
                {kitchenHandover.items?.map((item, idx) => (
                  <div key={idx} className="py-2 border-b last:border-0">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-700">{item.name}</span>
                      <span className="font-semibold text-lg">
                        {item.quantity} <span className="text-sm text-slate-500">{item.unit}</span>
                      </span>
                    </div>
                    {item.tare_weight > 0 && (
                      <div className="text-sm text-slate-500 mt-1 flex gap-3 justify-end">
                        <span>Tára: <span className="text-red-600 font-medium">{item.tare_weight.toFixed(2)} {item.unit}</span></span>
                        <span>→</span>
                        <span>Čistá: <span className="text-green-700 font-medium">{item.net_weight.toFixed(2)} {item.unit}</span></span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            
            {kitchenHandover.notes && (
              <div className="mt-4 p-3 bg-white rounded-lg border">
                <p className="text-sm text-slate-500">Poznámka z kuchyně:</p>
                <p className="text-slate-700">{kitchenHandover.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Corrections section */}
        {showCorrections && (
          <Card className="mb-6 border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-blue-700">
                <Edit3 className="w-5 h-5" />
                Korekce přijatého množství
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-600 mb-4">
                Zadejte skutečně přijaté množství, pokud se liší od předávky:
              </p>
              <div className="space-y-3">
                {kitchenHandover.items?.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <span className="w-40 text-slate-700">{item.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">
                        Předáno: {item.quantity} {item.unit}
                        {item.tare_weight > 0 && (
                          <span className="text-xs text-slate-400 ml-1">(čistá: {item.net_weight.toFixed(2)})</span>
                        )}
                      </span>
                      <span className="text-slate-400">→</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={corrections[item.item_id] || ''}
                        onChange={(e) => setCorrections(prev => ({
                          ...prev,
                          [item.item_id]: e.target.value
                        }))}
                        className="w-24"
                      />
                      <span className="text-sm text-slate-500">{item.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Poznámka ke korekci
                  {hasCorrections() && <span className="text-red-500 ml-1">*</span>}
                </label>
                <Textarea
                  placeholder="Důvod rozdílu..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className={hasCorrections() && !notes.trim() ? 'border-red-300 focus:border-red-500' : ''}
                />
                {hasCorrections() && !notes.trim() && (
                  <p className="text-red-500 text-xs mt-1">Při zadání korekce je poznámka povinná</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex flex-col sm:flex-row justify-end gap-3">
          {!showCorrections ? (
            <>
              <Button
                variant="outline"
                onClick={() => setShowCorrections(true)}
              >
                <Edit3 className="w-4 h-4 mr-2" />
                Zadat korekci množství
              </Button>
              <Button
                onClick={() => handleVerify(false)}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {saving ? 'Ukládám...' : 'Potvrdit převzetí a pokračovat'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => setShowCorrections(false)}
              >
                Zrušit korekci
              </Button>
              <Button
                onClick={() => handleVerify(true)}
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {saving ? 'Ukládám...' : 'Potvrdit s korekcí a pokračovat'}
              </Button>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BalirnaVerifyPage;
